import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { BusinessSettings } from '../../../../../model/business/BusinessSettings';
import EmailPreview from './EmailPreview';
import SmsPreview from './SmsPreview';
import VariableChips from './VariableChips';
import TemplatePickerModal from './TemplatePickerModal';
import { NotifEvent, SettingsField, defaultEmailBody, defaultSmsBody } from './notificationEvents';
import { templatesFor, TemplateVariant } from './notificationTemplates';

interface Props {
    event: NotifEvent;
    settingsData: BusinessSettings | null;
    onToggle: (field: SettingsField, checked: boolean) => void;
    onText: (field: SettingsField, value: string) => void;
    onInsertVar: (field: SettingsField, token: string) => void;
    /** Stores a textarea element so variable insertion can target the cursor. */
    registerTextRef: (field: SettingsField, el: HTMLTextAreaElement | null) => void;
    previewBusinessId: number | null;
    previewBusinessName: string | null;
}

const read = (s: BusinessSettings | null, f?: SettingsField): any =>
    f ? (s as any)?.[f] : undefined;

/**
 * Renders one notification event: the channel toggles plus, when the event has
 * editable copy, an e-mail and/or SMS editor with variable chips, a one-click
 * template and a live preview. Data-driven so every event stays consistent and
 * the existing Cypress `data-cy` selectors are preserved.
 */
const NotificationEventCard: React.FC<Props> = ({
    event,
    settingsData,
    onToggle,
    onText,
    onInsertVar,
    registerTextRef,
    previewBusinessId,
    previewBusinessName,
}) => {
    const { t, i18n } = useTranslation();
    // Templates in the business's language (i18n mirrors business.language).
    const tmpl = templatesFor(i18n.language)[event.key];

    // Which template picker (if any) is open: the target field + its variants.
    const [picker, setPicker] = useState<{
        field: SettingsField;
        title: string;
        variants: TemplateVariant[];
    } | null>(null);

    // The text editor + preview for a channel only make sense while that channel
    // is enabled — hide them when the owner unchecks E-mail / SMS.
    const emailOn = Boolean(read(settingsData, event.toggles.email));
    const smsOn = Boolean(read(settingsData, event.toggles.sms));

    // Push is NOT a business-configurable channel: mobile push is opt-in per
    // consumer device (managed in the client app), so a business toggle here was
    // dead. SMS is wired end-to-end in the UI but the sender is still a stub —
    // show it disabled with an "em breve" badge instead of pretending it sends.
    const CHANNEL_META: Array<{ key: keyof NotifEvent['toggles']; label: string; cy: string; disabled?: boolean; badge?: string }> = [
        { key: 'email', label: t('settings.notification.channels.email'), cy: 'email' },
        { key: 'sms', label: t('settings.notification.channels.sms'), cy: 'sms', disabled: true, badge: t('settings.notification.channels.comingSoon') },
        { key: 'inApp', label: t('settings.notification.channels.inApp'), cy: 'inapp' },
    ];

    return (
        <div className="notif-event-card">
            <div className="notif-event-card__head">
                <h4 className="notif-event-card__title">{t(event.labelKey)}</h4>
                {event.hintKey && <p className="notif-event-card__hint">{t(event.hintKey)}</p>}
            </div>

            {/* Channel toggles */}
            <div className="notif-channels" data-cy={`biz-notif-${event.cy}-channels`}>
                {CHANNEL_META.map(({ key, label, cy, disabled, badge }) => {
                    const field = event.toggles[key];
                    if (!field) return null;
                    return (
                        <span className={`notif-channel${disabled ? ' notif-channel--disabled' : ''}`} key={key}>
                            <label>
                                <input
                                    type="checkbox"
                                    data-cy={`biz-notif-${event.cy}-${cy}-toggle`}
                                    checked={Boolean(read(settingsData, field))}
                                    disabled={disabled}
                                    onChange={(e) => onToggle(field, e.target.checked)}
                                />
                            </label>
                            <span>{label}</span>
                            {badge && <span className="notif-channel__badge">{badge}</span>}
                        </span>
                    );
                })}
            </div>

            {/* E-mail editor — only while the E-mail channel is on */}
            {event.emailText && emailOn && (
                <div className="notif-channel-editor">
                    <div className="notif-channel-editor__bar">
                        <label className="notif-channel-editor__label" htmlFor={`txt-${event.emailText}`}>
                            {t('settings.notification.emailText')}
                        </label>
                        {tmpl?.email?.length ? (
                            <button
                                type="button"
                                className="notif-template-btn"
                                data-cy={`biz-notif-${event.cy}-email-template`}
                                onClick={() =>
                                    setPicker({
                                        field: event.emailText!,
                                        title: `Templates — ${t('settings.notification.emailText')}`,
                                        variants: tmpl.email!,
                                    })
                                }
                            >
                                <FontAwesomeIcon icon={faWandMagicSparkles} className="notif-template-btn__icon" />
                                {t('settings.notification.useTemplate')}
                            </button>
                        ) : null}
                    </div>
                    <VariableChips onInsert={(token) => onInsertVar(event.emailText!, token)} />
                    <textarea maxLength={2000}
                        id={`txt-${event.emailText}`}
                        ref={(el) => registerTextRef(event.emailText!, el)}
                        className="gs-email-textarea"
                        rows={7}
                        data-cy={`biz-notif-${event.cy}-email-text`}
                        value={(read(settingsData, event.emailText) as string) || ''}
                        placeholder={t('settings.notification.blankUsesDefault')}
                        onChange={(e) => onText(event.emailText!, e.target.value)}
                    />
                    <small className="notif-char-count">
                        {((read(settingsData, event.emailText) as string) || '').length}/2000
                    </small>
                    <EmailPreview
                        category={event.preview.category}
                        title={event.preview.title}
                        body={(read(settingsData, event.emailText) as string) || defaultEmailBody(event.key, i18n.language)}
                        businessId={previewBusinessId}
                    />
                </div>
            )}

            {/* SMS editor — only while the SMS channel is on */}
            {event.smsText && smsOn && (
                <div className="notif-channel-editor">
                    <div className="notif-channel-editor__bar">
                        <label className="notif-channel-editor__label" htmlFor={`txt-${event.smsText}`}>
                            {t('settings.notification.smsText')}
                        </label>
                        {tmpl?.sms?.length ? (
                            <button
                                type="button"
                                className="notif-template-btn"
                                data-cy={`biz-notif-${event.cy}-sms-template`}
                                onClick={() =>
                                    setPicker({
                                        field: event.smsText!,
                                        title: `Templates — ${t('settings.notification.smsText')}`,
                                        variants: tmpl.sms!,
                                    })
                                }
                            >
                                <FontAwesomeIcon icon={faWandMagicSparkles} className="notif-template-btn__icon" />
                                {t('settings.notification.useTemplate')}
                            </button>
                        ) : null}
                    </div>
                    <VariableChips onInsert={(token) => onInsertVar(event.smsText!, token)} />
                    <textarea maxLength={300}
                        id={`txt-${event.smsText}`}
                        ref={(el) => registerTextRef(event.smsText!, el)}
                        className="gs-email-textarea"
                        rows={4}
                        data-cy={`biz-notif-${event.cy}-sms-text`}
                        value={(read(settingsData, event.smsText) as string) || ''}
                        placeholder={t('settings.notification.blankUsesDefault')}
                        onChange={(e) => onText(event.smsText!, e.target.value)}
                    />
                    <small className="notif-char-count">
                        {((read(settingsData, event.smsText) as string) || '').length}/300
                    </small>
                    <SmsPreview
                        body={(read(settingsData, event.smsText) as string) || defaultSmsBody(event.key, i18n.language)}
                        businessName={previewBusinessName}
                    />
                </div>
            )}

            {picker && (
                <TemplatePickerModal
                    title={picker.title}
                    variants={picker.variants}
                    currentText={(read(settingsData, picker.field) as string) || ''}
                    onApply={(text) => {
                        onText(picker.field, text);
                        setPicker(null);
                    }}
                    onClose={() => setPicker(null)}
                />
            )}
        </div>
    );
};

export default NotificationEventCard;
